import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AdImageCropDialog } from "../../components/admin/AdImageCropDialog";
import { AD_IMAGE_HEIGHT, AD_IMAGE_WIDTH } from "../../lib/ads";
import { fileToDataUrl } from "../../lib/imageCrop";

const EMPTY_EDIT = {
  headline: "",
  stubLabel: "AFFILIATE AD",
  href: "",
  keyword: "",
  adType: "EMERGENT",
  active: true,
  image: null,
};

export default function AdminAds() {
  const [params] = useSearchParams();
  const passcode = params.get("passcode") || "";
  const navigate = useNavigate();

  const [ads, setAds] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [editing, setEditing] = React.useState(null);
  const [editForm, setEditForm] = React.useState(EMPTY_EDIT);
  const [saving, setSaving] = React.useState(false);
  const [editPreviewUrl, setEditPreviewUrl] = React.useState("");
  const [editCropSource, setEditCropSource] = React.useState("");
  const [editCropSourceName, setEditCropSourceName] = React.useState("");
  const [editCropSourceType, setEditCropSourceType] = React.useState("image/jpeg");
  const [editCropOpen, setEditCropOpen] = React.useState(false);

  React.useEffect(() => {
    return () => {
      if (editPreviewUrl && editPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(editPreviewUrl);
      }
    };
  }, [editPreviewUrl]);

  const setEditPreview = (nextUrl) => {
    if (editPreviewUrl && editPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(editPreviewUrl);
    }
    setEditPreviewUrl(nextUrl || "");
  };

  const fetchAds = React.useCallback(async () => {
    if (!passcode) return;
    setLoading(true);
    try {
      const { getAdminAds } = await import("../../lib/api");
      const data = await getAdminAds(passcode);
      setAds(Array.isArray(data?.items) ? data.items : []);
      setError("");
    } catch (_) {
      setError("Failed to fetch ads. Check passcode.");
    } finally {
      setLoading(false);
    }
  }, [passcode]);

  React.useEffect(() => {
    fetchAds();
  }, [fetchAds]);

  const filteredAds = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ads;
    return ads.filter((ad) => {
      const source = `${ad.headline || ""} ${ad.stubLabel || ""} ${ad.keyword || ""} ${ad.adType || ""} ${ad.href || ""}`.toLowerCase();
      return source.includes(q);
    });
  }, [ads, search]);

  const removeAd = async (adId) => {
    if (!passcode) return;
    const ok = window.confirm("Remove this ad from rotation?");
    if (!ok) return;
    try {
      const { deleteAdminAd } = await import("../../lib/api");
      await deleteAdminAd(adId, passcode);
      setAds((prev) => prev.filter((ad) => ad.id !== adId));
    } catch (_) {
      setError("Failed to remove ad.");
    }
  };

  const openEdit = (ad) => {
    setEditing(ad);
    setEditForm({
      headline: ad.headline || "",
      stubLabel: ad.stubLabel || "AFFILIATE AD",
      href: ad.href || "",
      keyword: ad.keyword || "",
      adType: ad.adType || "EMERGENT",
      active: !!ad.active,
      image: null,
    });
    setEditPreview(ad.imageUrl || "");
  };

  const closeEdit = () => {
    setEditing(null);
    setEditForm(EMPTY_EDIT);
    setEditPreview("");
  };

  const onEditImagePicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    try {
      const imageDataUrl = await fileToDataUrl(file);
      setEditCropSource(imageDataUrl);
      setEditCropSourceName(file.name || "ad-image");
      setEditCropSourceType(file.type || "image/jpeg");
      setEditCropOpen(true);
      setError("");
    } catch (err) {
      setError(err?.message || "Unable to open image cropper.");
    }
  };

  const onEditCropCancel = () => {
    setEditCropOpen(false);
    setEditCropSource("");
    setEditCropSourceName("");
    setEditCropSourceType("image/jpeg");
  };

  const onEditCropConfirm = async (croppedFile) => {
    setEditForm((p) => ({ ...p, image: croppedFile }));
    setEditPreview(URL.createObjectURL(croppedFile));
    onEditCropCancel();
  };

  const clearEditSelectedImage = () => {
    setEditForm((p) => ({ ...p, image: null }));
    setEditPreview(editing?.imageUrl || "");
  };

  const saveEdit = async () => {
    if (!editing || !passcode) return;
    setSaving(true);
    try {
      const { updateAdminAd } = await import("../../lib/api");
      const fd = new FormData();
      fd.append("headline", editForm.headline);
      fd.append("stubLabel", editForm.stubLabel);
      fd.append("href", editForm.href);
      fd.append("keyword", editForm.keyword);
      fd.append("adType", editForm.adType);
      fd.append("active", String(!!editForm.active));
      if (editForm.image) {
        fd.append("image", editForm.image);
      }
      await updateAdminAd(editing.id, fd, passcode);
      await fetchAds();
      closeEdit();
    } catch (_) {
      setError("Failed to update ad.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10 space-y-6" data-testid="admin-ads-page">
      <Card className="border-cyan-400/30 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 shadow-[0_0_35px_rgba(34,211,238,0.14)]">
        <CardHeader>
          <CardTitle className="text-slate-100" data-testid="admin-ads-title">Ad Rotation Manager</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!passcode && <div className="text-sm text-slate-300" data-testid="admin-ads-missing-passcode">Missing passcode. Go to /admin to generate links.</div>}

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by keyword, ad type, headline"
              className="w-full sm:max-w-sm rounded-full border border-cyan-400/40 bg-black/40 px-4 py-2 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-cyan-300/40"
              data-testid="admin-ads-search-input"
            />
            <button
              onClick={() => navigate(`/admin/ads/new?passcode=${encodeURIComponent(passcode)}`)}
              className="rounded-full border border-cyan-300/50 px-4 py-2 text-sm font-semibold text-cyan-100 bg-cyan-500/10 hover:bg-cyan-500/20 transition"
              disabled={!passcode}
              data-testid="admin-ads-add-button"
            >
              + Add Ad
            </button>
          </div>

          {loading && <div className="text-sm text-slate-300" data-testid="admin-ads-loading">Loading…</div>}
          {error && <div className="text-sm text-rose-300" data-testid="admin-ads-error">{error}</div>}

          {!loading && !error && (
            <div className="space-y-4" data-testid="admin-ads-pill-list">
              {filteredAds.length === 0 && <div className="text-sm text-slate-300" data-testid="admin-ads-empty">No ads found.</div>}
              {filteredAds.map((ad) => (
                <div key={ad.id} className="rounded-[30px] border border-cyan-300/30 bg-black/45 px-4 py-4 backdrop-blur" data-testid={`admin-ad-pill-${ad.id}`}>
                  <div className="flex flex-col md:flex-row gap-4 md:items-center">
                    <div className="shrink-0 rounded-2xl overflow-hidden border border-cyan-300/30 bg-black/30 w-full md:w-28 h-24">
                      <img src={ad.imageUrl} alt={ad.headline} className="w-full h-full object-cover" data-testid={`admin-ad-image-${ad.id}`} />
                    </div>

                    <div className="flex-1 min-w-0 space-y-3">
                      <div className="text-sm md:text-base font-semibold text-slate-100 truncate" data-testid={`admin-ad-headline-${ad.id}`}>{ad.headline}</div>
                      <div className="flex flex-wrap gap-2" data-testid={`admin-ad-info-pills-${ad.id}`}>
                        <InfoPill label="Type" value={ad.adType || "GENERAL"} color="cyan" testId={`admin-ad-type-${ad.id}`} />
                        <InfoPill label="Stub" value={ad.stubLabel || "AD"} color="sky" testId={`admin-ad-stub-${ad.id}`} />
                        <InfoPill label="Keyword" value={ad.keyword || "-"} color="emerald" testId={`admin-ad-keyword-${ad.id}`} />
                        <InfoPill label="Status" value={ad.active ? "ACTIVE" : "PAUSED"} color={ad.active ? "lime" : "rose"} testId={`admin-ad-status-${ad.id}`} />
                        <InfoPill label="Unique Clicks" value={String(ad.clicksUnique || 0)} color="amber" testId={`admin-ad-unique-clicks-${ad.id}`} />
                        <InfoPill label="All Clicks" value={String(ad.clicksTotal || 0)} color="amber" testId={`admin-ad-total-clicks-${ad.id}`} />
                      </div>
                      <div className="text-xs text-slate-300 break-all" data-testid={`admin-ad-link-${ad.id}`}>{ad.href}</div>
                    </div>

                    <div className="flex gap-2 md:self-start">
                      <button
                        onClick={() => openEdit(ad)}
                        className="rounded-full border border-cyan-200/50 bg-cyan-400/15 px-3 py-1.5 text-xs font-semibold text-cyan-100 hover:bg-cyan-400/25"
                        data-testid={`admin-ad-edit-${ad.id}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => removeAd(ad.id)}
                        className="rounded-full border border-rose-300/50 bg-rose-400/10 px-3 py-1.5 text-xs font-semibold text-rose-100 hover:bg-rose-400/20"
                        data-testid={`admin-ad-remove-${ad.id}`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(open) => !open && closeEdit()}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-cyan-300/40 bg-slate-950 text-slate-100" data-testid="admin-ad-edit-modal">
          <DialogHeader>
            <DialogTitle data-testid="admin-ad-edit-title">Edit ad</DialogTitle>
            <DialogDescription className="text-slate-300">Update link, image, and ad metadata from this panel.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <input value={editForm.headline} onChange={(e) => setEditForm((p) => ({ ...p, headline: e.target.value }))} className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm" placeholder="Headline" data-testid="admin-ad-edit-headline" />
            <input value={editForm.stubLabel} onChange={(e) => setEditForm((p) => ({ ...p, stubLabel: e.target.value }))} className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm" placeholder="AFFILIATE AD" data-testid="admin-ad-edit-stub-label" />
            <input value={editForm.href} onChange={(e) => setEditForm((p) => ({ ...p, href: e.target.value }))} className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm" placeholder="https://..." data-testid="admin-ad-edit-link" />

            <div className="grid sm:grid-cols-2 gap-3">
              <input value={editForm.keyword} onChange={(e) => setEditForm((p) => ({ ...p, keyword: e.target.value }))} className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm" placeholder="keyword" data-testid="admin-ad-edit-keyword" />
              <input value={editForm.adType} onChange={(e) => setEditForm((p) => ({ ...p, adType: e.target.value.toUpperCase() }))} className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm" placeholder="EMERGENT" data-testid="admin-ad-edit-type" />
            </div>

            <label className="text-xs text-slate-300" data-testid="admin-ad-edit-image-label">
              Replace image
              <input type="file" accept="image/*" onChange={onEditImagePicked} className="mt-1 block w-full text-xs" data-testid="admin-ad-edit-image" />
            </label>
            <div className="text-xs text-slate-400" data-testid="admin-ad-edit-image-specs">Crop output: {AD_IMAGE_WIDTH} × {AD_IMAGE_HEIGHT}px</div>

            {editPreviewUrl ? (
              <div className="space-y-2" data-testid="admin-ad-edit-image-preview-wrap">
                <div className="max-h-64 overflow-hidden rounded-2xl border border-cyan-300/30 bg-black/50" style={{ aspectRatio: `${AD_IMAGE_WIDTH} / ${AD_IMAGE_HEIGHT}` }}>
                  <img src={editPreviewUrl} alt="Ad preview" className="h-full w-full object-cover" data-testid="admin-ad-edit-image-preview" />
                </div>
                {editForm.image ? (
                  <button type="button" onClick={clearEditSelectedImage} className="rounded-full border border-slate-600 px-3 py-1.5 text-xs" data-testid="admin-ad-edit-image-clear">
                    Revert to current image
                  </button>
                ) : null}
              </div>
            ) : null}

            <label className="inline-flex items-center gap-2 text-sm text-slate-200" data-testid="admin-ad-edit-active-toggle">
              <input type="checkbox" checked={editForm.active} onChange={(e) => setEditForm((p) => ({ ...p, active: e.target.checked }))} />
              Active in rotation
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={closeEdit} className="rounded-full border border-slate-600 px-4 py-2 text-sm" data-testid="admin-ad-edit-cancel">Cancel</button>
            <button onClick={saveEdit} disabled={saving} className="rounded-full border border-cyan-300/50 bg-cyan-500/15 px-4 py-2 text-sm font-semibold text-cyan-100" data-testid="admin-ad-edit-save">
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AdImageCropDialog
        open={editCropOpen}
        imageSrc={editCropSource}
        sourceFileName={editCropSourceName}
        sourceMimeType={editCropSourceType}
        onCancel={onEditCropCancel}
        onConfirm={onEditCropConfirm}
      />
    </div>
  );
}

function InfoPill({ label, value, color, testId }) {
  const colorMap = {
    cyan: "bg-cyan-500/20 border-cyan-300/40 text-cyan-100",
    emerald: "bg-emerald-500/20 border-emerald-300/40 text-emerald-100",
    fuchsia: "bg-fuchsia-500/20 border-fuchsia-300/40 text-fuchsia-100",
    lime: "bg-lime-500/20 border-lime-300/40 text-lime-100",
    rose: "bg-rose-500/20 border-rose-300/40 text-rose-100",
    amber: "bg-amber-500/20 border-amber-300/40 text-amber-100",
    sky: "bg-sky-500/20 border-sky-300/40 text-sky-100",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${colorMap[color] || colorMap.cyan}`} data-testid={testId}>
      <span className="opacity-80">{label}:</span>
      <span className="font-semibold">{value}</span>
    </span>
  );
}